/**
 * La tienda, sin React.
 *
 * Todo lo que decide el catalogo publico y que se puede probar sin montar nada:
 * como se agrupan los productos en secciones, que dice la ficha de un usado,
 * que hay en la consulta, y el mensaje que se manda por WhatsApp.
 *
 * ESTO NO ES UN CHECKOUT
 * ----------------------
 * La "consulta" junta lo que el cliente quiere y lo manda escrito por WhatsApp.
 * No cobra, no reserva stock y no toca la caja. Un carrito que reserva obliga a
 * liberar lo que nadie vino a buscar, y eso es un problema que todavia no hace
 * falta tener.
 */
import { formatear, normalizarTelefonoAr } from "@tora/shared";

import { colorDeIphone, coloresDelModelo } from "@/components/catalogo/colores";
import type { ClaseOferta } from "@/lib/tipos";

// ─────────────────────────────────────────────────────────────────────────────
// Lo que sirve el backend
// ─────────────────────────────────────────────────────────────────────────────

export interface ProductoPublico {
  id: number;
  nombre: string;
  descripcion: string | null;
  rubro: string;
  rubro_slug: string;
  imagen_url: string | null;
  precio_ars: string;
  precio_usd: string;
  /** La moneda en que esta cotizado (0152). En USD la tienda muestra tambien los dolares. */
  precio_moneda?: "ARS" | "USD";
  disponibilidad: "DISPONIBLE" | "A_PEDIDO" | "SIN_STOCK";
  /** Lo unico que se dice del stock. El numero exacto no se publica. */
  en_local: boolean;
  /** Viaja al mensaje de WhatsApp para que quien atiende lo encuentre. */
  codigo_interno: string;
  es_usado: boolean;
  /**
   * Si este usado es UNA unidad concreta o un modelo de la lista de precios.
   *
   * Con IMEI hay un telefono, y de ese telefono se puede decir la bateria que
   * tiene. Sin IMEI es un renglon de la lista —"iPhone 13 128 GB usado"— que se
   * consigue a pedido: todavia no hay aparato del que medir nada (0059).
   */
  es_unidad: boolean;
  // La ficha del usado. En un sellado son todos null, y en un modelo tambien.
  bateria_pct: number | null;
  color: string | null;
  memoria_gb: number | null;
  caja_cable: boolean | null;
  garantia_meses: number | null;
  /**
   * Ademas de publicado, esta en el home (0093). Sin buscar ni elegir
   * categoria, el cliente ve solo lo que tiene esto; el resto lo encuentra.
   */
  en_home: boolean;
  /** El lugar entre los destacados del home (0095), o null si no esta. */
  orden_destacado: number | null;
  /**
   * El color cargado de un SELLADO (0129), o null. Un mismo iPhone puede estar
   * en varias filas de la misma memoria —la de la lista, sin color, y las que
   * entraron por compra con el suyo—: la tienda las junta en un solo boton y
   * con esto sabe cual mandar cuando el cliente elige un color.
   */
  color_sellado?: string | null;
  /**
   * En la fila de la lista (sin color) que esta en cero, los colores que SI
   * estan en el local. Lo que la pone «en stock» son esas unidades, no ella:
   * el color que no esta aca se pide (`enElColor`).
   */
  colores_en_local?: string[] | null;
  /** Si se consigue a pedido, sin contar lo que hay en el local. */
  a_pedido?: boolean;
}

export interface NegocioPublico {
  nombre: string;
  whatsapp: string | null;
  instagram: string | null;
  direccion: string | null;
  localidad: string | null;
  logo_url: string | null;
  horario: string | null;
  garantia_sellado_meses: number | null;
  garantia_usado_meses: number | null;
  dias_a_pedido: number | null;
  /** El titulo y la bajada del home (0093). `null` es el texto de siempre. */
  titulo_home: string | null;
  bajada_home: string | null;
  /** Las secciones del home en orden y con su titulo propio (0095). */
  secciones_home: SeccionConfigurada[] | null;
  /** El nombre de una tarjeta en la tienda, por su clave (0095). */
  nombres_tienda: Record<string, string> | null;
  /** Las secciones armadas a mano, con sus productos visibles en orden (0104). */
  secciones_propias: SeccionPropia[] | null;
}

export interface SeccionPropia {
  id: number;
  titulo: string;
  /** Los ids en orden. Varias memorias del mismo iPhone van juntas. */
  productos: number[];
}

export interface SeccionConfigurada {
  clave: string;
  /** `null` es el titulo de siempre (el nombre del rubro). */
  titulo: string | null;
  /** `false`: no va al home (0104). Sin el campo, se ve. */
  visible?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Las secciones
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Las secciones, en el orden en que se leen.
 *
 * El orden no es estetico: primero lo que el cliente se puede llevar hoy,
 * despues lo que hay que pedir. Un usado va a "usados" aunque este en el local,
 * porque se elige distinto: por unidad y no por modelo.
 *
 * `porRubro` reemplazo a una unica seccion "Sellados". Con treinta iPhone
 * alcanzaba una grilla; con Mac, iPad, Watch y AirPods adentro eran treinta y
 * ocho tarjetas de cosas que no se comparan entre si. Ahora cada rubro es su
 * seccion, en el orden que el local decide en su pantalla.
 */
export interface GrupoRubro {
  slug: string;
  nombre: string;
  productos: ProductoPublico[];
}

export interface Secciones {
  enLocal: ProductoPublico[];
  porRubro: GrupoRubro[];
  usados: ProductoPublico[];
  accesorios: ProductoPublico[];
}

/** Los rubros que son accesorios y no equipos. */
const RUBROS_ACCESORIO = new Set([
  "cables-y-adaptadores",
  "cargadores-y-baterias",
  "fundas-y-proteccion",
  "soportes",
  "audio",
  "accesorios",
]);

/**
 * Lo que el nombre dice que es, cuando el rubro no ayuda.
 *
 * Una funda y un templado estan cargados en el rubro "Apple", junto a los
 * iPhone, y en el sistema tiene sentido: son productos Apple. En la tienda no:
 * sin esto, la seccion "En stock" —la que deberia mostrar el telefono que te
 * podes llevar hoy— empieza con cinco protectores de pantalla, porque son los
 * que tienen stock.
 *
 * Es una lectura del nombre y no una regla de negocio, a proposito: mover esos
 * productos de rubro arreglaria la tienda y desordenaria el sistema, donde esa
 * clasificacion se usa para otras cosas. Si alguna vez se reordenan los rubros,
 * esta lista sobra y se borra.
 */
const NOMBRES_DE_ACCESORIO =
  /^(funda|template|templado|vidrio|protector|cargador|cable|adaptador|airtag|pencil|mouse|teclado|soporte|correa|malla)/i;

function esAccesorio(p: ProductoPublico): boolean {
  return RUBROS_ACCESORIO.has(p.rubro_slug) || NOMBRES_DE_ACCESORIO.test(p.nombre.trim());
}

/**
 * La categoria de los accesorios que el rubro no dice (ver `categoriaDe`).
 *
 * Con guion bajo adelante para que no pueda ser el slug de un rubro. Era
 * "accesorios", el mismo del rubro Accesorios, y el nombre del rubro se
 * mezclaba con el de la categoria. Hoy el rubro Accesorios entra entero aca.
 */
export const CATEGORIA_ACCESORIOS = "_accesorios";

/**
 * En que categoria del menu va un producto.
 *
 * La de su rubro, salvo un accesorio, que va a "Accesorios". Es el mismo
 * criterio que `agruparEnSecciones`: antes el home los separaba pero la
 * categoria no, y "iPhone 24" en el menu abria trece iPhone y once fundas,
 * templados y cargadores.
 *
 * Y es TODO accesorio, tambien los de los rubros de accesorios (Audio, Cables,
 * Cargadores). Antes esos iban a su rubro: el home mostraba una seccion
 * Accesorios con veinte, el menu una categoria Accesorios con diez, y tocar
 * «Audio» abria una seccion titulada «Accesorios» con dos. Una sola categoria,
 * la misma que la seccion del home, con el mismo nombre y la misma cuenta.
 */
export function categoriaDe(p: ProductoPublico): { slug: string; nombre: string } {
  if (!p.es_usado && esAccesorio(p)) {
    return { slug: CATEGORIA_ACCESORIOS, nombre: "Accesorios" };
  }
  return { slug: p.rubro_slug, nombre: p.rubro };
}

export function agruparEnSecciones(productos: readonly ProductoPublico[]): Secciones {
  const secciones: Secciones = { enLocal: [], porRubro: [], usados: [], accesorios: [] };
  // El orden de los rubros es el que ya trae la consulta (`rubro_orden`), asi
  // que alcanza con respetar el orden de aparicion.
  const rubros = new Map<string, GrupoRubro>();

  for (const p of productos) {
    if (p.es_usado) secciones.usados.push(p);
    else if (esAccesorio(p)) secciones.accesorios.push(p);
    else if (p.en_local) secciones.enLocal.push(p);
    else {
      const grupo = rubros.get(p.rubro_slug);
      if (grupo) grupo.productos.push(p);
      else rubros.set(p.rubro_slug, { slug: p.rubro_slug, nombre: p.rubro, productos: [p] });
    }
  }

  secciones.porRubro = [...rubros.values()];
  return secciones;
}

/** «iPhone 13 128 GB», sin el «(USADO)» que hoy viene pegado al nombre. */
export function nombreLimpio(nombre: string): string {
  return nombre
    .replace(/\s*\(\s*usad[oa]s?\s*\)\s*/gi, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/**
 * Como se escribe cada palabra que en la base esta en mayusculas.
 *
 * Las 380 fichas se cargaron desde planillas y quedaron gritadas: "IPHONE 14
 * 128GB". En una pantalla de trabajo da igual, pero el catalogo lo lee un
 * cliente y las mayusculas sostenidas se leen mas lento.
 *
 * El diccionario existe porque `IPHONE` en minusculas con la primera en mayuscula
 * da "Iphone", que esta mal escrito. Lo que no figura se capitaliza, y lo que
 * tiene numeros se deja como vino: "128GB" y "20W" ya estan bien.
 */
const COMO_SE_ESCRIBE: Record<string, string> = {
  iphone: "iPhone",
  ipad: "iPad",
  ipod: "iPod",
  imac: "iMac",
  airpods: "AirPods",
  airtag: "AirTag",
  macbook: "MacBook",
  magsafe: "MagSafe",
  watch: "Watch",
  mac: "Mac",
  usb: "USB",
  "usb-c": "USB-C",
  hdmi: "HDMI",
  anc: "ANC",
  nfc: "NFC",
  led: "LED",
  gb: "GB",
  tb: "TB",
  mm: "mm",
  pro: "Pro",
  max: "Max",
  plus: "Plus",
  mini: "Mini",
  air: "Air",
  se: "SE",
  ultra: "Ultra",
};

/** Palabras de union: van en minuscula salvo que abran el nombre. */
const CONECTORES = new Set(["de", "del", "con", "para", "y", "a", "en"]);

export function nombreLegible(nombre: string): string {
  return conAlternativo(legibleSinMarca(nombre));
}

/**
 * «AAA» es como el proveedor marca lo que no es original. Al lado de los
 * originales no le decia nada al cliente: en la tienda va «(alternativo)».
 */
function conAlternativo(nombre: string): string {
  return nombre
    .replace(/\s*["“”']?\baaa\b["“”']?/gi, " (alternativo)")
    .replace(/\s+/g, " ")
    .trim();
}

function legibleSinMarca(nombre: string): string {
  const limpio = nombreLimpio(nombre);
  // Si ya viene escrito a mano, con minusculas, no se toca.
  if (limpio !== limpio.toUpperCase()) return limpio;

  return limpio
    .split(" ")
    .map((palabra, i) => {
      const clave = palabra.toLowerCase();
      // Una marca se escribe SIEMPRE como se escribe, aunque abra el nombre:
      // "iPhone 14", no "IPhone 14".
      const marca = COMO_SE_ESCRIBE[clave];
      if (marca) return marca;
      if (CONECTORES.has(clave))
        return i === 0 ? clave.charAt(0).toUpperCase() + clave.slice(1) : clave;
      // "128GB" y "20W" ya estan bien escritos.
      if (/\d/.test(palabra)) return palabra;
      return palabra.charAt(0) + palabra.slice(1).toLowerCase();
    })
    .join(" ");
}

/**
 * La linea de datos de un usado: «Medianoche. Batería 89 %.»
 *
 * Solo entra lo que esta cargado. La vista del catalogo ya no publica un usado
 * al que le falte algo, asi que en la practica vienen todos, pero la funcion no
 * inventa lo que no hay.
 */
/**
 * Lo mismo, como renglones de una tabla: «Batería | 93 %».
 *
 * La ficha de la tienda sacaba la etiqueta de la primera palabra del texto, y
 * salia «DEEP | Deep Blue», «6 | 6 meses de garantía» o «Con | Con caja y
 * cable». La etiqueta se dice aca, junto al dato.
 */
export function datosDeUsado(p: ProductoPublico): { etiqueta: string; valor: string }[] {
  const datos: { etiqueta: string; valor: string }[] = [];
  if (p.color) datos.push({ etiqueta: "Color", valor: p.color });
  if (p.bateria_pct !== null) datos.push({ etiqueta: "Batería", valor: `${p.bateria_pct} %` });
  if (p.garantia_meses !== null) {
    datos.push({
      etiqueta: "Garantía",
      valor: p.garantia_meses === 1 ? "1 mes" : `${p.garantia_meses} meses`,
    });
  }
  if (p.caja_cable !== null) {
    datos.push({ etiqueta: "Caja", valor: p.caja_cable ? "Con caja y cable" : "Sin caja" });
  }
  return datos;
}

export function fichaDeUsado(p: ProductoPublico): string[] {
  const datos: string[] = [];
  if (p.color) datos.push(p.color);
  if (p.bateria_pct !== null) datos.push(`Batería ${p.bateria_pct} %`);
  if (p.garantia_meses !== null) {
    datos.push(
      p.garantia_meses === 1 ? "1 mes de garantía" : `${p.garantia_meses} meses de garantía`,
    );
  }
  if (p.caja_cable !== null) datos.push(p.caja_cable ? "Con caja y cable" : "Sin caja");
  return datos;
}

// ─────────────────────────────────────────────────────────────────────────────
// La consulta
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Una linea guarda el precio del momento en que se agrego.
 *
 * Es lo que queda en el telefono, pero no lo que se muestra ni se manda: la
 * consulta vuelve dias despues y el precio se mueve con el dolar. El panel la
 * pasa por `consultaAlDia`, que le pone el precio de hoy —el mismo que dice la
 * tarjeta— y aparta lo que ya no se publica. La fecha va en el mensaje para
 * que una diferencia entre ese dia y el de la charla se pueda conversar.
 */
export interface LineaConsulta {
  id: number;
  nombre: string;
  codigo: string;
  /** Precio unitario en pesos, como se vio. */
  precio: number;
  cantidad: number;
  /** «Sellado», «Usado, Medianoche, 89 %». Para que el mensaje se entienda solo. */
  detalle: string;
  /**
   * Si es UNA sola y no se puede pedir de a dos.
   *
   * Lo unico es la unidad, no todo usado: de "iPhone 13 128 GB usado", que es un
   * renglon de la lista, se pueden querer dos. Del que tiene IMEI hay uno solo.
   */
  esUnico: boolean;
  /**
   * El color que eligio el cliente, si eligio. Con el se sabe si ESE color esta
   * en el local: la fila de la lista esta «en stock» si hay alguno.
   */
  color?: string | null;
  /**
   * «A pedido» o «Sin stock», o nada si esta en el local. Lo pone
   * `consultaAlDia` con el catalogo de hoy: lo guardado en el telefono puede
   * ser de otro dia, o de antes de que existiera el campo.
   */
  estado?: string | null;
}

export type Consulta = LineaConsulta[];

/**
 * El cartel de disponibilidad de una tarjeta, una ficha o una linea.
 *
 * La vista distingue tres cosas (`disponibilidad`) y la tienda decia una sola:
 * «EN STOCK», o nada. Un Pencil sin stock se veia igual que el AirTag que esta
 * en el local, y los Mac que no hay igual que los iPhone que se consiguen a
 * pedido. Con varias filas (una tarjeta de generacion) gana lo mejor que haya:
 * si una esta en el local, esta en stock; si alguna se pide, se pide.
 */
export function estadoDeDisponibilidad(
  productos: readonly ProductoPublico[],
): "EN STOCK" | "A PEDIDO" | "SIN STOCK" {
  if (productos.some((p) => p.en_local)) return "EN STOCK";
  if (productos.some((p) => p.disponibilidad === "A_PEDIDO")) return "A PEDIDO";
  return "SIN STOCK";
}

/** Lo mismo para una linea de la consulta: en el local no hace falta decirlo. */
function estadoDeLinea(p: ProductoPublico): string | null {
  const estado = estadoDeDisponibilidad([p]);
  if (estado === "A PEDIDO") return "A pedido";
  if (estado === "SIN STOCK") return "Sin stock";
  return null;
}

/** « (a, b)» con lo que haya; nada si no hay nada: «Funda ()» no se manda. */
function entreParentesis(partes: ReadonlyArray<string | null | undefined>): string {
  const hay = partes.filter(Boolean);
  return hay.length > 0 ? ` (${hay.join(", ")})` : "";
}

export function detalleDeLinea(p: ProductoPublico): string {
  // «Sellado» separa un iPhone nuevo de uno usado; en una funda o un cargador
  // no separa nada y el cliente no sabe que quiere decir.
  if (!p.es_usado) return esAccesorio(p) ? "" : "Sellado";
  const partes = ["Usado"];
  if (p.color) partes.push(p.color);
  if (p.bateria_pct !== null) partes.push(`${p.bateria_pct} %`);
  return partes.join(", ");
}

export function aLinea(p: ProductoPublico, color?: string | null): LineaConsulta {
  return {
    id: p.id,
    nombre: nombreLegible(p.nombre),
    codigo: p.codigo_interno,
    precio: Number(p.precio_ars) || 0,
    cantidad: 1,
    detalle: detalleConColor(p, color),
    esUnico: p.es_unidad,
    color: color ?? null,
    estado: estadoDeLinea(enElColor(p, color)),
  };
}

/**
 * Suma uno. Una UNIDAD no suma: hay un solo telefono con ese IMEI, y pedir dos
 * no significa nada. Un modelo de la lista si suma, como un sellado.
 */
export function agregar(consulta: Consulta, p: ProductoPublico, color?: string | null): Consulta {
  const existente = consulta.find((l) => l.id === p.id);
  if (!existente) return [...consulta, aLinea(p, color)];
  if (existente.esUnico) return consulta;
  // El color que se elige ultimo es el que vale: la linea es una sola y pedir el
  // mismo equipo en dos colores es un caso que no vale la pena modelar.
  const detalle = color ? detalleConColor(p, color) : existente.detalle;
  const deColor = color ?? existente.color ?? null;
  return consulta.map((l) =>
    l.id === p.id ? { ...l, cantidad: l.cantidad + 1, detalle, color: deColor } : l,
  );
}

/** Resta uno. En cero, la linea desaparece: una linea con cero no dice nada. */
export function quitar(consulta: Consulta, id: number): Consulta {
  return consulta
    .map((l) => (l.id === id ? { ...l, cantidad: l.cantidad - 1 } : l))
    .filter((l) => l.cantidad > 0);
}

export function sacar(consulta: Consulta, id: number): Consulta {
  return consulta.filter((l) => l.id !== id);
}

export function cantidadDe(consulta: Consulta, id: number): number {
  return consulta.find((l) => l.id === id)?.cantidad ?? 0;
}

export function cuantosProductos(consulta: Consulta): number {
  return consulta.reduce((a, l) => a + l.cantidad, 0);
}

export function total(consulta: Consulta): number {
  return consulta.reduce((a, l) => a + l.precio * l.cantidad, 0);
}

/**
 * La consulta contra el catalogo de hoy.
 *
 * El panel decia «Precios de hoy» y mostraba —y mandaba— el precio del dia en
 * que se agrego cada cosa, y un producto que se despublico seguia en el total
 * y en el mensaje. Aca cada linea toma el precio de hoy, y la que ya no esta
 * en el catalogo queda aparte: se dice que no esta y no se manda.
 *
 * Sin catalogo cargado (`null`) no se juzga nada: en el primer instante no hay
 * ningun producto, y tachar la consulta entera por eso seria mentir.
 */
export function consultaAlDia(
  consulta: Consulta,
  catalogo: ReadonlyMap<number, ProductoPublico> | null,
): { vigentes: Consulta; noDisponibles: Consulta } {
  if (!catalogo) return { vigentes: consulta, noDisponibles: [] };
  const vigentes: Consulta = [];
  const noDisponibles: Consulta = [];
  for (const l of consulta) {
    const p = catalogo.get(l.id);
    // El estado del color de la linea: la fila de la lista dice «en stock»
    // con cualquier color en el local, y el mensaje mandaba «Black» sin «a
    // pedido» cuando la que habia era la Ultramarine.
    if (p)
      vigentes.push({
        ...l,
        precio: Number(p.precio_ars) || 0,
        estado: estadoDeLinea(enElColor(p, l.color)),
      });
    else noDisponibles.push(l);
  }
  return { vigentes, noDisponibles };
}

// ─────────────────────────────────────────────────────────────────────────────
// El mensaje
// ─────────────────────────────────────────────────────────────────────────────

function fechaCorta(hoy: Date): string {
  const dd = String(hoy.getDate()).padStart(2, "0");
  const mm = String(hoy.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}`;
}

/**
 * El texto que se manda por WhatsApp.
 *
 * TRES COSAS QUE PARECEN DE MAS Y NO LO SON
 * -----------------------------------------
 *  · El CODIGO de cada producto: quien atiende lo pega en el buscador y arma el
 *    ticket sin volver a preguntar cual era. Con los usados es imprescindible,
 *    porque son unidades unicas con nombres casi iguales.
 *  · El TOTAL: la conversacion arranca sobre una cifra y no sobre una lista.
 *  · La FECHA de los precios: el precio se mueve con el dolar. Sin fecha, el
 *    mismo mensaje una semana despues es una discusion.
 *
 * Se muestra entero en pantalla antes de enviarlo. Nadie toca un boton que
 * escribe en su nombre sin ver que dice.
 */
export function mensajeDeConsulta(
  consulta: Consulta,
  hoy = new Date(),
  /**
   * El equipo que el cliente entrega como parte de pago: `null` si no entrega,
   * "" si entrega y no dijo cual. Va en el mensaje para que el local no tenga
   * que preguntarlo: el pie lo promete y antes no habia como decirlo.
   */
  entrega: string | null = null,
): string {
  if (consulta.length === 0) return "Hola! Te hago una consulta.";
  const conEntrega =
    entrega === null
      ? []
      : [
          "",
          entrega.trim()
            ? `Quiero entregar mi equipo como parte de pago: ${entrega.trim()}.`
            : "Quiero entregar mi equipo como parte de pago.",
        ];

  const lineas = consulta.map(
    (l) =>
      `${l.cantidad}x ${l.nombre}${entreParentesis([l.detalle, l.estado?.toLowerCase()])} - ${formatear(l.precio * l.cantidad, "ARS")} [${l.codigo}]`,
  );

  return [
    "Hola! Te consulto por estos productos:",
    "",
    ...lineas,
    ...conEntrega,
    "",
    `Total: ${formatear(total(consulta), "ARS")}`,
    `Precios que vi el ${fechaCorta(hoy)}.`,
  ].join("\n");
}

/**
 * El numero en el formato que exige wa.me: solo digitos, con pais.
 *
 * Los numeros argentinos se cargan de mil formas ("3401 41-3384", "+54 9 3401
 * 413384", "0341 15 555-1234"). Lo resuelve `normalizarTelefonoAr`, la misma
 * que usa el remito: antes aca solo se sacaban los no digitos y se anteponia
 * 549, y el formato local (con el 0 y el 15) armaba wa.me/5490341155551234, un
 * numero que no existe.
 */
export function numeroWhatsapp(crudo: string | null | undefined): string | null {
  return normalizarTelefonoAr(crudo)?.e164 ?? null;
}

export function linkWhatsapp(numero: string | null, texto: string): string | null {
  if (!numero) return null;
  return `https://wa.me/${numero}?text=${encodeURIComponent(texto)}`;
}

/** Consulta por UN producto, desde su tarjeta o su ficha. */
export function mensajeDeProducto(p: ProductoPublico): string {
  const nombre = nombreLegible(p.nombre);
  return `Hola! Me interesa el ${nombre}${entreParentesis([detalleDeLinea(p)])} que vi en el catálogo. ¿Está disponible?`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Los iPhone se agrupan por modelo
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Una variante de una generacion: "Pro Max", con sus capacidades.
 *
 * `clave` es el modelo completo ("18 PRO MAX") porque es lo que necesita la
 * tabla de colores; `etiqueta` es como se muestra en el selector.
 */
export interface VarianteModelo {
  clave: string;
  etiqueta: string;
  /** Un producto por capacidad, de menor a mayor. */
  capacidades: ProductoPublico[];
  /**
   * Todas las filas del modelo, tambien las que `capacidades` junto con otra
   * con el mismo nombre (ver `agruparModelos`). Las usa el color de la ficha
   * y el home, que marca la tarjeta entera.
   */
  todas: ProductoPublico[];
  enLocal: boolean;
  desde: number;
}

/**
 * Un modelo con sus capacidades.
 *
 * POR QUE AGRUPAR
 * ---------------
 * El iPhone 17 Pro en 256, 512 y 1 TB son tres productos en la base, con tres
 * precios y tres stocks, y eso esta bien: son tres cosas distintas que se
 * compran. Pero en la vidriera son UN telefono, y mostrarlos como tres tarjetas
 * con la misma foto y el mismo nombre hace que el catalogo parezca repetido y
 * obliga al cliente a comparar precios para darse cuenta de que cambia la
 * memoria.
 *
 * LA CAPACIDAD ES UN PRODUCTO, EL COLOR NO
 * ----------------------------------------
 * Elegir 512 GB es elegir OTRO producto: otro precio, otro stock. Elegir azul no
 * es elegir nada en la base, porque un sellado se pide y se pide en el color que
 * el cliente quiera (ver `catalogo/colores.ts`, que ya lo dice). Por eso la
 * capacidad selecciona una variante y el color es una preferencia que viaja en
 * el mensaje de WhatsApp.
 */
export interface GrupoModelo {
  /** "17 PRO MAX". Identifica el grupo y sirve para buscar sus colores. */
  clave: string;
  /** "iPhone 17 Pro Max", como se muestra. */
  nombre: string;
  /** La variante mas barata: es la que da la foto y el "desde". */
  principal: ProductoPublico;
  /** Todas las capacidades, de menor a mayor: una por memoria. */
  variantes: ProductoPublico[];
  /** Todas las filas, con las de un producto repetido (ver `agruparModelos`). */
  todas: ProductoPublico[];
  /** Si ALGUNA capacidad esta en el local. */
  enLocal: boolean;
  /** El precio de la variante mas barata, en pesos. */
  desde: number;
}

/** "17 PRO MAX" a "iPhone 17 Pro Max". */
export function nombreDeModelo(clave: string): string {
  return `iPhone ${nombreLegible(clave)}`.replace(/\s+/g, " ").trim();
}

/**
 * Separa "IPHONE 17 PRO MAX 256 GB" en modelo y capacidad.
 *
 * Es la misma lectura que hace el ERP en `iphones/logica.ts`; se repite aca en
 * diez lineas en vez de importarla para que la tienda no dependa de un modulo
 * de la herramienta interna, que se mueve por otras razones.
 */
export function partirNombreIphone(nombre: string): { modelo: string; gb: number } | null {
  if (!/^\s*iphone\b/i.test(nombre)) return null;
  const sinMarca = nombre.replace(/^\s*iphone\s*/i, "").trim();
  const m = /^(.*?)\s*(\d+)\s*(GB|TB)\b\s*(.*)$/i.exec(sinMarca);
  if (!m) return null;
  const n = Number(m[2]);
  return {
    modelo: (m[1] ?? "").trim().toUpperCase().replace(/\s+/g, " "),
    gb: (m[3] ?? "").toUpperCase() === "TB" ? n * 1024 : n,
  };
}

/** "256 GB", "1 TB". */
export function capacidadLegible(gb: number): string {
  return gb >= 1024 ? `${gb / 1024} TB` : `${gb} GB`;
}

/**
 * Lo mismo, pero para cualquier equipo: "MACBOOK AIR M4 13\" 16GB 512GB".
 *
 * Toma la ULTIMA capacidad del nombre, no la primera. Un Mac lleva dos —la
 * memoria y el disco— y la que distingue un producto de otro es la de atras: el
 * de 256 y el de 512 son el mismo Mac con otro disco. Tomando la primera, los
 * dos darian "16 GB" y la ficha mostraria dos botones iguales.
 *
 * En un iPhone hay una sola, asi que da lo mismo y sirve para los dos.
 */
export function partirCapacidad(nombre: string): { modelo: string; gb: number } | null {
  const m = /^(.*)\s(\d+)\s*(GB|TB)\b\s*(.*)$/i.exec(nombre.trim());
  if (!m) return null;
  const n = Number(m[2]);
  const modelo = (m[1] ?? "").trim().toUpperCase().replace(/\s+/g, " ");
  if (modelo === "") return null;
  return { modelo, gb: (m[3] ?? "").toUpperCase() === "TB" ? n * 1024 : n };
}

export function capacidadDe(p: ProductoPublico): number {
  return partirNombreIphone(p.nombre)?.gb ?? partirCapacidad(p.nombre)?.gb ?? 0;
}

/**
 * Agrupa los iPhone por modelo y deja el resto como estaba.
 *
 * Lo que no es un iPhone con capacidad en el nombre sale en un grupo de una sola
 * variante: asi la pantalla dibuja todo igual y no necesita dos caminos.
 */
/**
 * Que tan nuevo es un modelo, para ordenarlo.
 *
 * Primero la generacion, de mayor a menor: el 18 antes que el 17. Adentro de una
 * generacion, el tope primero (Pro Max, Pro, Plus, base), que es como los
 * muestra Apple y como los pide el que viene por lo ultimo.
 *
 * Lo que no es un iPhone va al fondo: no tiene generacion con que compararse.
 */
function loNuevoPrimero(a: GrupoModelo, b: GrupoModelo): number {
  const gen = (g: GrupoModelo): number => Number(/(\d+)/.exec(g.clave)?.[1] ?? -1);
  const rango = (g: GrupoModelo): number => {
    const c = g.clave.toUpperCase();
    if (c.includes("PRO MAX")) return 4;
    if (c.includes("PRO")) return 3;
    if (c.includes("PLUS")) return 2;
    if (/\bE\b|\bSE\b/.test(c)) return 0;
    return 1;
  };
  return gen(b) - gen(a) || rango(b) - rango(a) || a.nombre.localeCompare(b.nombre, "es");
}

/**
 * "18 PRO MAX" a { generacion: "18", variante: "PRO MAX" }.
 *
 * La generacion es lo que el cliente busca —"quiero el 18"— y la variante es lo
 * que elige despues. Un modelo sin numero (AIR, SE) es su propia generacion.
 */
export function partirModelo(modelo: string): { generacion: string; variante: string } {
  const m = /^(\d+E?)\s*(.*)$/.exec(modelo.trim());
  if (!m) return { generacion: modelo.trim(), variante: "" };
  return { generacion: m[1] ?? modelo, variante: (m[2] ?? "").trim() };
}

/** "PRO MAX" a "Pro Max"; lo vacio es el modelo base. */
export function etiquetaDeVariante(variante: string): string {
  if (variante === "") return "Normal";
  return nombreLegible(variante);
}

/**
 * Con que modelo se agrupa un producto. Tres formas de clave, y el prefijo dice
 * cual es:
 *   "17 PRO MAX"  un modelo de iPhone, que ademas tiene generacion y variante
 *   "=MAC MINI M4 16 GB"  cualquier otro equipo con capacidades: solo se
 *                          juntan sus discos, no tiene variantes que elegir
 *   "#123"  el producto solo, sin nada con que agruparse
 *
 * Una UNIDAD usada es siempre "#": es UN telefono con su bateria y su rayon,
 * y meterlo adentro del renglon de la lista de la misma capacidad lo
 * esconderia detras de un selector con dos veces "128 GB".
 */
function claveDeModeloDe(p: ProductoPublico): string {
  const iphone = p.es_unidad ? null : partirNombreIphone(p.nombre);
  const generico = iphone || p.es_unidad ? null : partirCapacidad(p.nombre);
  return iphone ? iphone.modelo : generico ? `=${generico.modelo}` : `#${p.id}`;
}

/**
 * La clave de la tarjeta en que aparece un producto: la generacion de un
 * iPhone ("18"), o la del modelo en lo demas. Es la clave de `nombres_tienda`.
 */
export function claveDeTarjeta(p: ProductoPublico): string {
  const modelo = claveDeModeloDe(p);
  if (modelo.startsWith("#") || modelo.startsWith("=")) return modelo;
  return partirModelo(modelo).generacion;
}

/**
 * El nombre de una fila, para saber si dos son el mismo producto: sin
 * mayusculas, sin espacios de mas y con la memoria pegada ("256 GB" y "256GB"
 * se cargan de las dos formas).
 */
function mismoNombre(nombre: string): string {
  return nombre
    .trim()
    .toUpperCase()
    .replace(/(\d)\s+(GB|TB)\b/g, "$1$2")
    .replace(/\s+/g, " ");
}

/**
 * Un boton por producto repetido.
 *
 * Un sellado puede estar cargado varias veces con el mismo nombre: la fila de
 * la lista, sin color, y las que entraron por compra con el suyo. La ficha
 * dibujaba un boton "256 GB" por fila —tres iguales— y el cliente no tenia como
 * distinguirlos. Queda una por nombre: la que esta en el local, si no la que
 * no tiene color (la de la lista), si no la mas barata. Las demas siguen en
 * `todas`, y el color que se elige en la ficha elige entre ellas
 * (`productoDelColor`).
 *
 * Por NOMBRE y no por memoria: "PS5 LECTORA 1 TB" y "PS5 LECTORA 1 TB + 2
 * JUEGOS" tienen la misma memoria y son dos productos. Juntandolos por memoria
 * el combo desaparecia de la ficha, y como una consola no tiene color no habia
 * como llegar a el.
 *
 * Solo en los sellados: un usado de la lista es otra cosa (0092) y no se toca.
 */
function unaPorNombre(ordenadas: readonly ProductoPublico[]): ProductoPublico[] {
  const elegidas = new Map<string, ProductoPublico>();
  const sueltas: ProductoPublico[] = [];
  const mejor = (a: ProductoPublico, b: ProductoPublico): boolean =>
    a.en_local !== b.en_local
      ? a.en_local
      : !a.color_sellado !== !b.color_sellado
        ? !a.color_sellado
        : Number(a.precio_ars) !== Number(b.precio_ars)
          ? Number(a.precio_ars) < Number(b.precio_ars)
          : a.id < b.id;
  for (const p of ordenadas) {
    if (p.es_usado || capacidadDe(p) === 0) {
      sueltas.push(p);
      continue;
    }
    const clave = mismoNombre(p.nombre);
    const ya = elegidas.get(clave);
    if (!ya || mejor(p, ya)) elegidas.set(clave, p);
  }
  const quedan = new Set([...elegidas.values(), ...sueltas]);
  return ordenadas.filter((p) => quedan.has(p));
}

/**
 * Lo que dice el boton de memoria de la ficha: "1 TB", o "1 TB + 2 Juegos" si
 * hay otro boton con la misma memoria. Sin el agregado, la PS5 sola y la que
 * viene con juegos eran dos botones "1 TB" iguales.
 */
export function etiquetaDeMemoria(p: ProductoPublico, botones: readonly ProductoPublico[]): string {
  const gb = capacidadDe(p);
  const base = capacidadLegible(gb);
  const repetida = botones.some((q) => q.id !== p.id && capacidadDe(q) === gb);
  if (!repetida) return base;
  const resto = /\d+\s*(?:GB|TB)\b\s*(.*)$/i.exec(p.nombre.trim())?.[1]?.trim() ?? "";
  return resto ? `${base} ${nombreLegible(resto)}` : base;
}

/**
 * Las filas del catalogo que son el mismo modelo que `elegido`, sin importar
 * en que seccion salgan.
 *
 * La ficha no puede buscar el color solo entre las filas de su tarjeta: en «En
 * stock» la tarjeta del 18 Pro tiene nada mas la Burgundy del local, porque la
 * de la lista y la Black estan en la seccion del rubro. Eligiendo Black, el
 * mensaje salia con el codigo de la Burgundy y la ficha decia EN STOCK.
 */
export function filasDelModelo(
  elegido: ProductoPublico,
  catalogo: readonly ProductoPublico[],
): ProductoPublico[] {
  const clave = claveDeModeloDe(elegido);
  const filas = catalogo.filter((p) => !p.es_usado && claveDeModeloDe(p) === clave);
  return filas.some((p) => p.id === elegido.id) ? filas : [elegido, ...filas];
}

/**
 * Todas las filas del catalogo de una generacion, salgan en la seccion que
 * salgan.
 *
 * La tarjeta del 18 se arma con las filas de SU seccion, y el 18 Pro Max Silver
 * del local va a «En stock»: la tarjeta decia A PEDIDO con uno en el local. El
 * cartel se calcula con esto, no con la tarjeta.
 */
export function filasDeLaGeneracion(
  grupo: GrupoGeneracion,
  catalogo: readonly ProductoPublico[],
): ProductoPublico[] {
  const suyas = grupo.variantes.flatMap((v) => v.todas);
  if (suyas.some((p) => p.es_usado)) return suyas;
  const claves = new Set(suyas.map(claveDeModeloDe));
  const otras = catalogo.filter(
    (p) => !p.es_usado && claves.has(claveDeModeloDe(p)) && !suyas.includes(p),
  );
  return [...suyas, ...otras];
}

/**
 * El color en que esta en el local ese mismo producto (misma memoria), si el
 * boton es la fila de la lista y la del local es otra.
 *
 * Sin color elegido la ficha decia A PEDIDO en el 18 Pro Max 256, y el Silver
 * estaba en el local: habia que adivinar el color para enterarse.
 */
export function colorEnElLocal(
  elegido: ProductoPublico,
  filas: readonly ProductoPublico[],
): string | null {
  if (elegido.en_local || elegido.es_usado) return null;
  const nombre = mismoNombre(elegido.nombre);
  const enLocal = filas.find(
    (p) => !p.es_usado && p.en_local && p.color_sellado && mismoNombre(p.nombre) === nombre,
  );
  return enLocal?.color_sellado ?? null;
}

/**
 * La fila que corresponde al color que eligio el cliente.
 *
 * El boton de memoria es UNA fila, pero puede haber otra del mismo producto
 * cargada con ese color: entonces va esa, con su codigo. Antes el mensaje
 * podia decir "Burgundy" con el codigo de la Black, y lo vendido a pedido se
 * vuelve encargo por codigo. Un color sin fila propia va con la fila sin color
 * (la de la lista), que es la que se pide en el color que se quiera.
 *
 * Si no hay ni fila de ese color ni fila sin color, da `null`: mandar la de
 * otro color ataba la unidad del local a un cliente que pidio otra cosa. La
 * ficha no ofrece esos colores.
 *
 * `todas` son las filas del modelo en todo el catalogo (`filasDelModelo`), no
 * las de la tarjeta. `etiquetaDe` lleva el color cargado ("BLACK") a como lo
 * muestra la ficha ("Black"); sin ella se comparan sin mayusculas ni acentos.
 */
export function productoDelColor(
  elegido: ProductoPublico,
  todas: readonly ProductoPublico[],
  color: string | null | undefined,
  etiquetaDe: (color: string) => string = (c) => c,
): ProductoPublico | null {
  if (!color || elegido.es_usado) return elegido;
  const nombre = mismoNombre(elegido.nombre);
  const hermanas = todas.filter((p) => !p.es_usado && mismoNombre(p.nombre) === nombre);
  const buscado = claveDeColor(color);
  const delColor = hermanas.find(
    (p) => p.color_sellado && claveDeColor(etiquetaDe(p.color_sellado)) === buscado,
  );
  if (delColor) return delColor;
  if (!elegido.color_sellado) return enElColor(elegido, color, etiquetaDe);
  const lista = hermanas.find((p) => !p.color_sellado);
  return lista ? enElColor(lista, color, etiquetaDe) : null;
}

/**
 * La fila de la lista, dicha para el color que eligio el cliente.
 *
 * La fila de la lista (sin color) esta en cero, y la vista la pone «en stock»
 * si hay unidades de algun color en el local (0125). Pero un color que no esta
 * entre esas unidades se pide: con una sola Ultramarine en el local, la ficha
 * decia EN STOCK en Black, White, Pink y Teal, y el mensaje mandaba la Black
 * sin «a pedido». Aca, si el color elegido no es de los que hay
 * (`colores_en_local`), la fila vuelve a decir lo que diria sin ellos.
 *
 * Sin color elegido, o con una fila que tiene color propio o stock propio,
 * queda como vino. `etiquetaDe` es la de `productoDelColor`; por defecto la de
 * la paleta de Apple, que es la que usa la ficha.
 */
export function enElColor(
  p: ProductoPublico,
  color: string | null | undefined,
  etiquetaDe: (color: string) => string = (c) => colorDeIphone(c)?.etiqueta ?? c,
): ProductoPublico {
  if (!color || p.es_usado || p.color_sellado || !p.colores_en_local) return p;
  const buscado = claveDeColor(color);
  if (p.colores_en_local.some((c) => claveDeColor(etiquetaDe(c)) === buscado)) return p;
  return { ...p, en_local: false, disponibilidad: p.a_pedido ? "A_PEDIDO" : "SIN_STOCK" };
}

export function agruparModelos(productos: readonly ProductoPublico[]): GrupoModelo[] {
  const grupos = new Map<string, ProductoPublico[]>();

  for (const p of productos) {
    const clave = claveDeModeloDe(p);
    const actual = grupos.get(clave);
    if (actual) actual.push(p);
    else grupos.set(clave, [p]);
  }

  const armados = [...grupos.entries()].map(([clave, filas]) => {
    const todas = [...filas].sort((a, b) => capacidadDe(a) - capacidadDe(b));
    const ordenadas = clave.startsWith("#") ? todas : unaPorNombre(todas);
    const principal = ordenadas.reduce((mas, p) =>
      Number(p.precio_ars) < Number(mas.precio_ars) ? p : mas,
    );
    return {
      clave,
      nombre: clave.startsWith("#")
        ? nombreLegible(principal.nombre)
        : clave.startsWith("=")
          ? nombreLegible(clave.slice(1))
          : nombreDeModelo(clave),
      principal,
      variantes: ordenadas,
      todas,
      enLocal: todas.some((p) => p.en_local),
      desde: Number(principal.precio_ars) || 0,
    };
  });

  return armados.sort(loNuevoPrimero);
}

/**
 * El detalle de una linea de la consulta, con el color que eligio el cliente.
 *
 * El color no identifica un producto, asi que no puede ir en el id: va en el
 * texto, que es donde lo va a leer quien atienda.
 */
export function detalleConColor(p: ProductoPublico, color?: string | null): string {
  return [detalleDeLinea(p), color].filter(Boolean).join(", ");
}

// ─────────────────────────────────────────────────────────────────────────────
// Una generacion, una tarjeta
// ─────────────────────────────────────────────────────────────────────────────

/**
 * El iPhone 18 entero: Normal, Pro y Pro Max, cada uno con sus memorias.
 *
 * POR QUE LA TARJETA ES LA GENERACION Y NO EL MODELO
 * --------------------------------------------------
 * El cliente no llega buscando "un 18 Pro Max de 512 en negro": llega buscando
 * "el 18". La variante, la memoria y el color son las tres preguntas que se
 * contestan DESPUES, y en ese orden, que es el mismo que usa Apple en su tienda.
 *
 * Con una tarjeta por modelo la grilla mostraba tres veces el mismo telefono, y
 * el cliente tenia que entender la diferencia entre Pro y Pro Max antes de poder
 * mirar nada.
 */
export interface GrupoGeneracion {
  /** "18". Identifica la generacion. */
  clave: string;
  /** "iPhone 18". */
  nombre: string;
  variantes: VarianteModelo[];
  /** La mas barata de toda la generacion: da la foto y el "desde". */
  principal: ProductoPublico;
  desde: number;
  enLocal: boolean;
}

/** Pro Max, Pro, Plus, base, e: de tope de gama para abajo. */
function rangoDeVariante(variante: string): number {
  const v = variante.toUpperCase();
  if (v.includes("PRO MAX")) return 4;
  if (v.includes("PRO")) return 3;
  if (v.includes("PLUS")) return 2;
  if (v === "E") return 0;
  return 1;
}

export function agruparGeneraciones(productos: readonly ProductoPublico[]): GrupoGeneracion[] {
  const porModelo = agruparModelos(productos);
  const generaciones = new Map<string, GrupoModelo[]>();

  for (const g of porModelo) {
    // Lo que no es un iPhone queda solo, como estaba.
    // Solo los iPhone tienen generacion con variantes adentro. Un Mac o un
    // producto suelto es su propio grupo: no hay un "MacBook Air" del que
    // elegir Pro o Pro Max.
    const esIphone = !g.clave.startsWith("#") && !g.clave.startsWith("=");
    const clave = esIphone ? partirModelo(g.clave).generacion : g.clave;
    const actual = generaciones.get(clave);
    if (actual) actual.push(g);
    else generaciones.set(clave, [g]);
  }

  const armadas = [...generaciones.entries()].map(([clave, modelos]) => {
    const variantes: VarianteModelo[] = modelos
      .map((m) => ({
        clave: m.clave,
        etiqueta:
          m.clave.startsWith("#") || m.clave.startsWith("=")
            ? m.nombre
            : etiquetaDeVariante(partirModelo(m.clave).variante),
        capacidades: m.variantes,
        todas: m.todas,
        enLocal: m.enLocal,
        desde: m.desde,
      }))
      .sort((a, b) => rangoDeVariante(b.clave) - rangoDeVariante(a.clave));

    const principal = modelos.reduce((mas, m) => (m.desde < mas.desde ? m : mas));

    // Una generacion con UNA sola variante se llama por esa variante.
    //
    // Sin esto, el unico 18 publicado —un Pro Max— se anunciaba como "iPhone
    // 18": la tarjeta no dibuja la lista de variantes cuando hay una sola, asi
    // que el "Pro Max" no aparecia en ninguna parte. Y como las secciones se
    // agrupan por separado, el Pro Max en el local y el Pro a pedido salian
    // como dos tarjetas con el mismo nombre y dos precios distintos, que se lee
    // como el mismo telefono cotizado dos veces.
    const sola = variantes.length === 1 ? variantes[0] : undefined;
    const sufijo = sola && sola.etiqueta !== "Normal" ? ` ${sola.etiqueta}` : "";

    return {
      clave,
      nombre:
        clave.startsWith("#") || clave.startsWith("=")
          ? principal.nombre
          : `iPhone ${clave}${sufijo}`,
      variantes,
      principal: principal.principal,
      desde: principal.desde,
      enLocal: modelos.some((m) => m.enLocal),
    };
  });

  // Mismo criterio que los modelos: lo mas nuevo primero.
  return armadas.sort((a, b) => {
    const gen = (g: GrupoGeneracion): number => Number(/(\d+)/.exec(g.clave)?.[1] ?? -1);
    return gen(b) - gen(a) || a.nombre.localeCompare(b.nombre, "es");
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// La foto que cambia con el color
// ─────────────────────────────────────────────────────────────────────────────
//
// La foto de un color cuelga del MODELO y no del producto: las tres capacidades
// de un 17 Pro se ven exactamente igual, y lo unico que cambia la imagen es el
// color (migracion 0057). Estas tres funciones son el puente entre lo que el
// cliente toco en la pantalla y el nombre con el que esa foto quedo guardada.

/** Una fila de `/publico/imagenes/modelos`: que hay, sin los bytes. */
export interface FotoDeModelo {
  modelo: string;
  color: string;
  version: number;
}

/**
 * El modelo tal como lo nombra la ruta de fotos: "17-pro-max".
 *
 * Tiene que dar EXACTAMENTE lo mismo que `claveDeModelo` de
 * `scripts/fotos-apple.ts`, que es quien guarda las fotos. Si las dos lecturas
 * se separan, la foto existe y nadie la encuentra.
 */
export function claveDeModelo(nombre: string): string | null {
  const partes = partirNombreIphone(nombre);
  if (!partes || partes.modelo === "") return null;
  return partes.modelo.toLowerCase().replace(/\s+/g, "-");
}

/**
 * El color como quedo guardado: "Deep Blue" → "deep-blue".
 *
 * Se sacan los acentos y todo lo que no sea letra o numero porque la clave
 * viaja en una URL, y "(PRODUCT)RED" con parentesis adentro de un path es un
 * pedido roto antes de salir del navegador.
 */
export function claveDeColor(etiqueta: string): string | null {
  const clave = etiqueta
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return clave === "" ? null : clave;
}

/**
 * La URL de la foto de ese equipo en ese color, o null si no la tenemos.
 *
 * Devuelve una ruta con barra al principio, igual que `imagen_url`, para que la
 * resuelva `srcImagen` y no haya dos formas de armar la direccion de una foto.
 *
 * El `?v=` no lo lee nadie en el servidor: esta para que reemplazar una foto no
 * deje la anterior en pantalla hasta que alguien recargue.
 */
export function fotoDeColor(
  fotos: readonly FotoDeModelo[],
  nombreProducto: string,
  colorEtiqueta: string | null,
): string | null {
  if (!colorEtiqueta) return null;
  const modelo = claveDeModelo(nombreProducto);
  const color = claveDeColor(colorEtiqueta);
  if (!modelo || !color) return null;

  const foto = fotos.find((f) => f.modelo === modelo && f.color === color);
  return foto ? `/publico/imagenes/modelo/${modelo}/${color}?v=${foto.version}` : null;
}

/**
 * La foto de un usado sin foto propia: la de su modelo en su color.
 *
 * Un usado publicado sin foto salia con el hueco gris de la camara, aunque la
 * foto de ese modelo en ese color estaba (la misma que usa la ficha de un
 * sellado cuando se elige el color). El usado tiene UN color, el suyo, asi que
 * no hay nada que elegir. Sin color cargado no se inventa: queda el hueco.
 * `etiquetaDe` lleva un color cargado con otro nombre («NEGRO») al de Apple.
 */
export function fotoDeUsado(
  fotos: readonly FotoDeModelo[],
  p: ProductoPublico,
  etiquetaDe: (color: string) => string = (c) => c,
): string | null {
  if (!p.es_usado || p.imagen_url || !p.color?.trim()) return null;
  return fotoDeColor(fotos, p.nombre, etiquetaDe(p.color));
}

/**
 * La foto de un sellado sin foto propia: la de su modelo, en el primer color
 * que la tenga.
 *
 * Un sellado se pide en el color que el cliente quiera, asi que cualquier color
 * del modelo lo muestra bien. Sin esto la tarjeta del 18 quedaba con el hueco
 * gris, y la ficha mostraba la del 18 al elegir un color: la foto estaba y la
 * tarjeta no la encontraba. El orden es el de la paleta de Apple
 * (`coloresDelModelo`), para que sea siempre la misma foto; si ninguno de esos
 * tiene, la primera que haya de ese modelo. Un usado no pasa por aca: viene
 * del color que vino (`fotoDeUsado`).
 */
export function fotoDeModelo(fotos: readonly FotoDeModelo[], p: ProductoPublico): string | null {
  if (p.es_usado || p.imagen_url) return null;
  for (const c of coloresDelModelo(p.nombre)) {
    const foto = fotoDeColor(fotos, p.nombre, c.etiqueta);
    if (foto) return foto;
  }
  const modelo = claveDeModelo(p.nombre);
  const cualquiera = modelo
    ? [...fotos]
        .filter((f) => f.modelo === modelo)
        .sort((x, y) => x.color.localeCompare(y.color))[0]
    : undefined;
  return cualquiera
    ? `/publico/imagenes/modelo/${cualquiera.modelo}/${cualquiera.color}?v=${cualquiera.version}`
    : null;
}

/** Los colores de ese modelo que tienen foto, para no prometer lo que no hay. */
export function coloresConFoto(
  fotos: readonly FotoDeModelo[],
  nombreProducto: string,
): Set<string> {
  const modelo = claveDeModelo(nombreProducto);
  if (!modelo) return new Set();
  return new Set(fotos.filter((f) => f.modelo === modelo).map((f) => f.color));
}

// ─────────────────────────────────────────────────────────────────────────────
// Ofertas
// ─────────────────────────────────────────────────────────────────────────────
//
// La unica parte de la pantalla donde el local habla en vez de enumerar. Una
// oferta NO es un producto: no tiene stock, no se agrega al carrito y su precio
// es el de su propia fila (migracion 0060). El boton abre WhatsApp preguntando
// por ella, que es como se cierra igual.

/** Los siete tipos de oferta (0100); la definicion vive en `lib/tipos`. */
export type { ClaseOferta };

export interface OfertaPublica {
  id: number;
  clase: ClaseOferta;
  titulo: string;
  detalle: string | null;
  /** null en los tipos sin precio (canje, envio) o cuando no se cargo. */
  precio_ars: string | null;
  /** Lo que costaba antes, o lo que costaria por separado. Puede no haber. */
  precio_antes_ars: string | null;
  /** El codigo del producto del que habla, si la oferta apunta a uno. */
  codigo: string | null;
  /** El descuento de una oferta EFECTIVO, en %. */
  porcentaje: number | null;
  /** La foto propia de la oferta (0110). Sin ella, la tarjeta usa la del equipo. */
  imagen_url?: string | null;
}

const ETIQUETAS: Record<ClaseOferta, string> = {
  COMBO: "COMBO",
  BAJA: "BAJÓ DE PRECIO",
  ULTIMAS: "ÚLTIMAS UNIDADES",
  EFECTIVO: "EN EFECTIVO",
  PREVENTA: "PREVENTA",
  CANJE: "PLAN CANJE",
  ENVIO: "ENVÍO GRATIS",
};

/** El cartelito de arriba de la tarjeta. Son fijos a proposito. */
export function etiquetaDeOferta(clase: ClaseOferta): string {
  return ETIQUETAS[clase] ?? clase;
}

/**
 * Cuanto se ahorra, en pesos. Null si la oferta no dice contra que.
 *
 * Se calcula y no se guarda: guardarlo seria un tercer numero que hay que
 * mantener de acuerdo con los otros dos, y el dia que no lo este, la tarjeta
 * miente sobre plata.
 */
export function ahorroDeOferta(o: OfertaPublica): number | null {
  if (o.precio_antes_ars === null || o.precio_ars === null) return null;
  const ahorro = Number(o.precio_antes_ars) - Number(o.precio_ars);
  return Number.isFinite(ahorro) && ahorro > 0 ? ahorro : null;
}

/** El mensaje con el que se pregunta por una oferta. */
export function mensajeDeOferta(o: OfertaPublica): string {
  // Sin precio (un canje, el envio) el mensaje no inventa uno: un "$ 0" en el
  // WhatsApp de quien atiende se lee como un error de la pagina.
  //
  // En una oferta EFECTIVO `precio_ars` es el de LISTA: el mensaje ponia ese
  // numero al lado de "10 % off en efectivo", y no el que la tarjeta destaca.
  const lista = Number(o.precio_ars) || 0;
  const efectivo = precioEnEfectivo(o);
  const precio =
    efectivo !== null
      ? ` - ${formatear(efectivo, "ARS")} en efectivo (lista ${formatear(lista, "ARS")})`
      : o.precio_ars !== null
        ? ` - ${formatear(lista, "ARS")}`
        : "";
  const cola = o.codigo ? ` [${o.codigo}]` : "";
  return `Hola! Me interesa esta oferta del catálogo:\n\n${o.titulo}${precio}${cola}`;
}

/** El precio pagando en efectivo, en pesos: el de lista menos su porcentaje. */
export function precioEnEfectivo(o: OfertaPublica): number | null {
  if (o.clase !== "EFECTIVO" || o.precio_ars === null || !o.porcentaje) return null;
  return Math.round((Number(o.precio_ars) * (100 - o.porcentaje)) / 100);
}

// ─────────────────────────────────────────────────────────────────────────────
// Las categorias
// ─────────────────────────────────────────────────────────────────────────────

export interface CategoriaPublica {
  slug: string;
  nombre: string;
  cuantos: number;
}

/**
 * Los rubros que de verdad tienen algo publicado, en el orden del catalogo.
 *
 * Se arman de los productos y no de una lista aparte: un rubro vacio en la
 * barra es un filtro que se toca y deja la pantalla en blanco.
 *
 * El orden es el que ya trae la consulta —`rubro_orden`, que el local decide en
 * su pantalla—, asi que alcanza con respetar el orden de aparicion. (El menu lo
 * acomoda despues como el home: `ordenDeCategorias`.)
 *
 * `cuantos` son las TARJETAS que se ven al elegirla, la suma de los contadores
 * de sus secciones: los equipos agrupados por generacion, como en la pantalla.
 * Antes se contaba producto por producto (cada memoria, cada fila), y el menu
 * decia «Mac 14» para abrir una seccion «Mac 10».
 */
export function categoriasDeCatalogo(productos: readonly ProductoPublico[]): CategoriaPublica[] {
  const vistos = new Map<string, { slug: string; nombre: string; productos: ProductoPublico[] }>();
  for (const p of productos) {
    const { slug, nombre } = categoriaDe(p);
    const actual = vistos.get(slug);
    if (actual) actual.productos.push(p);
    else vistos.set(slug, { slug, nombre, productos: [p] });
  }
  return [...vistos.values()].map(({ slug, nombre, productos: suyos }) => ({
    slug,
    nombre,
    cuantos: tarjetasQueSeVen(suyos),
  }));
}

/**
 * Cuantas tarjetas muestran las secciones con estos productos: lo que dice el
 * contador de cada una, sumado (ver `routes/catalogo.tsx`).
 */
function tarjetasQueSeVen(productos: readonly ProductoPublico[]): number {
  const s = agruparEnSecciones(productos);
  return (
    agruparGeneraciones(s.enLocal).length +
    s.porRubro.reduce((n, r) => n + agruparGeneraciones(r.productos).length, 0) +
    s.usados.length +
    s.accesorios.length
  );
}

/** Minusculas y sin acentos: «Cancelación» y «CANCELACION» son lo mismo. */
export function paraBuscar(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/**
 * Lo que una persona escribe y lo que dice el nombre del proveedor no siempre
 * es lo mismo: «auriculares» son AirPods, «protector» es un templado, «iPhone»
 * en un accesorio viene como «IP».
 */
const SINONIMOS: Record<string, readonly string[]> = {
  iphone: ["ip"],
  auricular: ["airpods", "auricular"],
  protector: ["templado", "vidrio", "protector"],
  vidrio: ["templado", "vidrio"],
  templado: ["templado", "vidrio"],
  funda: ["funda", "case"],
  case: ["funda", "case"],
  cargador: ["cargador", "fuente"],
};

/**
 * Las formas en que puede aparecer una palabra buscada.
 *
 * El plural se lleva al singular de los dos lados de la cuenta: «cargadores»
 * no contiene «cargador»… al reves si, pero «fundas» no esta en «Funda Leather».
 * Se saca la «s» o el «es» del final y se busca lo que queda. Los sinonimos
 * cortos («ip») van como palabra entera: «ip» suelto adentro de «chip» o de
 * «ipad» traeria cualquier cosa.
 */
function formasDe(palabra: string): RegExp[] {
  const escapar = (t: string): string => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const raiz =
    palabra.length > 4 && /[^aeiou]es$/.test(palabra)
      ? palabra.slice(0, -2)
      : palabra.length > 3 && palabra.endsWith("s")
        ? palabra.slice(0, -1)
        : palabra;
  const formas: RegExp[] = [new RegExp(escapar(raiz))];
  for (const s of SINONIMOS[raiz] ?? []) {
    formas.push(
      s.length <= 3 ? new RegExp(`[^a-z0-9]${escapar(s)}[^a-z0-9]`) : new RegExp(escapar(s)),
    );
  }
  return formas;
}

/**
 * Filtra por texto y por categoria. `null` en rubro es "todo".
 *
 * El texto se parte en palabras y cada una tiene que estar en algun lado: en
 * el nombre, en el rubro o en el nombre que el local le puso a la tarjeta
 * (`nombres`, 0095). Antes se buscaba la frase entera adentro del nombre del
 * sistema: «macbook air 13» no encontraba «MACBOOK AIR M4 13"», «airpods pro»
 * no encontraba «AIRPODS 3 PRO», una tarjeta renombrada no se encontraba por
 * lo que dice, y una tilde dejaba la busqueda vacia.
 */
export function filtrarCatalogo(
  productos: readonly ProductoPublico[],
  texto: string,
  rubro: string | null,
  nombres?: Record<string, string> | null,
): ProductoPublico[] {
  const palabras = paraBuscar(texto).split(/\s+/).filter(Boolean).map(formasDe);
  const coincide = (p: ProductoPublico): boolean => {
    if (palabras.length === 0) return true;
    // La categoria tambien: «accesorios» encuentra la funda aunque su rubro
    // diga «Apple».
    const donde = ` ${paraBuscar(
      [
        p.nombre,
        nombreLegible(p.nombre),
        p.rubro,
        categoriaDe(p).nombre,
        nombres?.[claveDeTarjeta(p)] ?? "",
        nombres?.[`#${p.id}`] ?? "",
      ].join(" "),
    )} `;
    return palabras.every((formas) => formas.some((f) => f.test(donde)));
  };
  return productos.filter((p) => (rubro === null || categoriaDe(p).slug === rubro) && coincide(p));
}

/**
 * El precio en dolares, solo si el producto se cotiza en dolares (0152).
 *
 * Un iPhone o una Mac se cotizan en USD y el cliente compara en dolares; un
 * perfume se cotiza en pesos y su equivalente en dolares es una cuenta que
 * nadie pidio. Por eso no se muestra en todo: solo donde es el precio de verdad.
 */
export function precioEnDolares(
  p: Pick<ProductoPublico, "precio_moneda" | "precio_usd">,
): string | null {
  if (p.precio_moneda !== "USD") return null;
  const usd = Number(p.precio_usd);
  return usd > 0 ? formatear(usd, "USD") : null;
}
